import React from 'react';
import { amountToWords } from './BillInvoicePreview';

// ── Stamp / sign — adjust all positioning here ────────────────────────────
const STAMP_WIDTH        = 120;   // px — stamp image width
const STAMP_HEIGHT       = 120;   // px — stamp image height
const SIGN_WIDTH         = 130;  // px — signature image width
const SIGN_HEIGHT        = 130;  // px — signature image height
const IMG_GAP            = 6;    // px — horizontal gap between stamp and sign
const FOR_LHHS_MB        = -30;    // px — gap between "For LHHS" line and images
const IMG_MB             = -40;    // px — gap between images and "Authorised Signatory" line
const FOOTER_SECTION_MT  = 24;   // px — gap between payment table and the whole footer row
// ──────────────────────────────────────────────────────────────────────────

export interface ReceiptEntryDoc {
  payment_method: string;
  amount: number | string;
  payment_date: string;
  reference_no?: string | null;
  notes?: string | null;
}

export interface BillDoc {
  id: number;
  /** NULL for consumables — they are numbered by invoice_no alone. */
  bill_no: number | null;
  invoice_no?: string | null;
  bill_date: string;
  invoice_period_start: string;
  invoice_period_end: string;
  total_amount: number;
  status: string;
}

export interface PatientDoc {
  id: number;
  patient_name: string;
  patient_salutation?: string;
  age: string;
  sex: string;
  treating_hospital?: string;
  treating_doctor?: string;
  treating_details?: { doctor: string; hospital: string; department: string }[];
  address: string;
  patient_clients: { salutation?: string; client_name: string; contact_number_1: string; email: string }[];
}

interface Props {
  bill: BillDoc;
  patient: PatientDoc;
  entries: ReceiptEntryDoc[];
  collectedBy?: string;
  paymentMadeBy?: string;
  receiptDate?: string;
}

function dmyFmt(d: string): string {
  if (!d) return '';
  const parts = d.split('-');
  if (parts.length === 3 && parts[0].length === 4) return `${parts[2]}-${parts[1]}-${parts[0]}`;
  return d;
}

function longFmt(d: string): string {
  if (!d) return '';
  return new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

function fmtNum(n: number): string {
  return n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function toNum(v: number | string): number {
  return typeof v === 'string' ? parseFloat(v) || 0 : v;
}

const PaymentReceiptDocument = React.forwardRef<HTMLDivElement, Props>(
  ({ bill, patient, entries, collectedBy = 'LHHS Team', paymentMadeBy, receiptDate }, ref) => {
    const client = patient.patient_clients?.[0];
    const clientName = client?.client_name || '';
    const defaultPaymentMadeBy = clientName
      ? `${client?.salutation ? client.salutation + ' ' : ''}${clientName}`.trim()
      : patient.patient_name;
    const totalReceived = entries.reduce((s, e) => s + toNum(e.amount), 0);
    const balance = bill.total_amount - totalReceived;
    const issueDate = receiptDate || new Date().toISOString().split('T')[0];

    const border     = '1px solid #333';
    const thinBorder = '1px solid #aaa';
    const lightBorder= '1px solid #ddd';

    const thStyle: React.CSSProperties = {
      border: thinBorder, padding: '4px 6px', backgroundColor: '#f0f0f0',
      fontWeight: 700, fontSize: '10px', textAlign: 'center',
    };
    const tdC: React.CSSProperties = { border: lightBorder, padding: '4px 6px', fontSize: '10px', textAlign: 'center' };
    const tdL: React.CSSProperties = { border: lightBorder, padding: '4px 6px', fontSize: '10px', textAlign: 'left' };
    const tdR: React.CSSProperties = { border: lightBorder, padding: '4px 6px', fontSize: '10px', textAlign: 'right' };
    const infoLabel: React.CSSProperties = {
      fontWeight: 700, fontSize: '10px', padding: '3px 8px',
      borderRight: thinBorder, color: '#222', whiteSpace: 'nowrap', width: '140px',
    };
    const infoValue: React.CSSProperties = { fontSize: '10px', padding: '3px 8px', color: '#333' };

    return (
      <div
        ref={ref}
        style={{
          width: '794px',
          height: '1123px',
          position: 'relative',
          fontFamily: 'Arial, Helvetica, sans-serif',
          backgroundColor: '#ffffff',
          overflow: 'hidden',
        }}
      >
        {/* Letterhead background — same as billing invoice */}
        <img
          src="/letterhead.png"
          alt=""
          crossOrigin="anonymous"
          style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'fill' }}
        />

        {/* Content overlay — same offsets as billing */}
        <div
          style={{
            position: 'absolute',
            top: '162px',
            left: '28px',
            right: '28px',
            bottom: '128px',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* ── Title row ── */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
            <span style={{ fontWeight: 800, fontSize: '13px', letterSpacing: '1px', color: '#149bc1' }}>
              ACKNOWLEDGEMENT OF PAYMENT
            </span>
            <span style={{ fontSize: '10px', fontWeight: 600 }}>
              Receipt Number: RECN-{bill.invoice_no || bill.bill_no}
            </span>
          </div>

          {/* ── Client info box ── */}
          <div style={{ border }}>
            <div style={{ display: 'flex' }}>
              {/* Left: address */}
              <div style={{ width: '38%', borderRight: border, padding: '8px', fontSize: '10px' }}>
                <div style={{ fontWeight: 700, marginBottom: '4px' }}>To,</div>
                <div style={{ fontWeight: 700 }}>
                  {patient.patient_salutation ? `${patient.patient_salutation} ` : ''}{patient.patient_name},
                </div>
                <div style={{ marginTop: '4px', lineHeight: 1.6, color: '#333', whiteSpace: 'pre-wrap' }}>
                  {patient.address || ''}
                </div>
                {client?.contact_number_1 && (
                  <div style={{ marginTop: '6px', fontWeight: 600 }}>
                    Contact Number {client.contact_number_1}
                  </div>
                )}
              </div>

              {/* Right: info grid + bill details */}
              <div style={{ width: '62%' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <tbody>
                    {[
                      ['Customer ID', `LHHS${patient.id}`],
                      ['Receipt Issue Date', dmyFmt(issueDate)],
                      ['Payment Made By', paymentMadeBy || defaultPaymentMadeBy],
                      ['Payment Collected By', collectedBy],
                    ].map(([label, value], i) => (
                      <tr key={i} style={{ borderBottom: thinBorder }}>
                        <td style={infoLabel}>{label}</td>
                        <td style={infoValue}>{value}</td>
                      </tr>
                    ))}

                    {/* Nested bill details table */}
                    <tr>
                      <td colSpan={2} style={{ padding: 0 }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                          <thead>
                            <tr>
                              {['Bill Date', 'Invoice No.', 'Invoices Period', 'Amount(INR)'].map((h) => (
                                <th key={h} style={{ ...thStyle, textAlign: h === 'Amount(INR)' ? 'right' : 'center' }}>
                                  {h}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td style={tdC}>{dmyFmt(bill.bill_date)}</td>
                              <td style={tdC}>{bill.invoice_no || bill.bill_no}</td>
                              <td style={tdC}>
                                {longFmt(bill.invoice_period_start)} to {longFmt(bill.invoice_period_end)}
                              </td>
                              <td style={{ ...tdR }}>{fmtNum(bill.total_amount)}</td>
                            </tr>
                            <tr>
                              <td colSpan={3} style={{ ...tdR, fontWeight: 700, borderRight: 'none' }}>
                                Total Nursing Bill :
                              </td>
                              <td style={{ ...tdR, fontWeight: 700 }}>{fmtNum(bill.total_amount)}</td>
                            </tr>
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* ── Payment Receipt Details table ── */}
          <div style={{ border, borderTop: 'none' }}>
            <div style={{
              borderBottom: thinBorder, padding: '4px 8px', textAlign: 'center',
              fontWeight: 700, fontSize: '11px', backgroundColor: '#f9f9f9',
            }}>
              Payment Receipt Details
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ ...thStyle, width: '6%' }}>SL.No</th>
                  <th style={{ ...thStyle, width: '13%' }}>Received Date</th>
                  <th style={{ ...thStyle, width: '17%' }}>Mode Of Payment</th>
                  <th style={{ ...thStyle, width: '48%', textAlign: 'center' }}>Transaction Details</th>
                  <th style={{ ...thStyle, width: '16%', textAlign: 'right' }}>Amount(INR)</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((e, idx) => {
                  const txnDetails = [e.reference_no, e.notes].filter(Boolean).join(' — ');
                  return (
                    <tr key={idx}>
                      <td style={tdC}>{idx + 1}</td>
                      <td style={tdC}>{dmyFmt(e.payment_date)}</td>
                      <td style={tdC}>{e.payment_method}</td>
                      <td style={tdL}>{txnDetails || '—'}</td>
                      <td style={tdR}>{fmtNum(toNum(e.amount))}</td>
                    </tr>
                  );
                })}

                {/* Total received */}
                <tr style={{ backgroundColor: '#fafafa' }}>
                  <td
                    colSpan={4}
                    style={{
                      border: thinBorder, padding: '5px 8px', fontSize: '10px', fontWeight: 700,
                      borderTop: '2px solid #333',
                    }}
                  >
                    Total Payment Received INR ({amountToWords(Math.round(totalReceived))})
                  </td>
                  <td
                    style={{
                      border: thinBorder, padding: '5px 8px', fontSize: '10px', fontWeight: 800,
                      textAlign: 'right', borderTop: '2px solid #333',
                    }}
                  >
                    {fmtNum(totalReceived)}
                  </td>
                </tr>

                {/* Balance */}
                <tr style={{ backgroundColor: balance <= 0 ? '#f0fdf4' : '#fff7ed' }}>
                  <td
                    colSpan={4}
                    style={{ border: thinBorder, padding: '5px 8px', fontSize: '10px', fontWeight: 700 }}
                  >
                    Balance Due INR : {balance <= 0 ? '0' : fmtNum(balance)}
                  </td>
                  <td
                    style={{
                      border: thinBorder, padding: '5px 8px', fontSize: '10px', fontWeight: 800,
                      textAlign: 'right', color: balance <= 0 ? '#15803d' : '#dc2626',
                    }}
                  >
                    {balance <= 0 ? 'NILL' : fmtNum(balance)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* ── Declaration / Signature ── */}
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end',
            marginTop: `${FOOTER_SECTION_MT}px`, fontSize: '9px',
          }}>
            {/* Customer signature */}
            <div style={{ textAlign: 'center' }}>
              <div style={{ borderTop: '1px solid #555', marginTop: '30px', paddingTop: '4px', width: '120px' }}>
                Customer Signature
              </div>
            </div>

            {/* Declaration */}
            <div style={{ textAlign: 'center', maxWidth: '280px', lineHeight: 1.6, color: '#333' }}>
              <div style={{ fontWeight: 700, marginBottom: '4px' }}>Declaration :</div>
              <div>This is a computer generated acknowledgement of payment.</div>
              <div>We declare that this acknowledgement shows that</div>
              <div>actual payment received and that all particulars are true and correct.</div>
              <div>Not require additional stamp or signature.</div>
            </div>

            {/* Stamp + Signature */}
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontWeight: 700, marginBottom: `${FOR_LHHS_MB}px` }}>For LHHS Healthcare Pvt. Ltd.</div>
              <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-end', gap: `${IMG_GAP}px`, marginBottom: `${IMG_MB}px` }}>
                <img
                  crossOrigin="anonymous"
                  src="/assets/stamp.png"
                  alt="stamp"
                  style={{ width: STAMP_WIDTH, height: STAMP_HEIGHT, objectFit: 'contain', mixBlendMode: 'multiply' }}
                />
                <img
                  crossOrigin="anonymous"
                  src="/assets/sign.png"
                  alt="signature"
                  style={{ width: SIGN_WIDTH, height: SIGN_HEIGHT, objectFit: 'contain', mixBlendMode: 'multiply' }}
                />
              </div>
              <div style={{ borderTop: '1px solid #555', paddingTop: '4px', width: '130px', marginLeft: 'auto' }}>
                Authorised Signatory
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
);

PaymentReceiptDocument.displayName = 'PaymentReceiptDocument';
export default PaymentReceiptDocument;
